/**
 * Berechtigungskonzept V3.0 — shared value-set types (WS0 foundation).
 *
 * The enumerated dimensions the WS0 schema introduces as text/smallint columns +
 * CHECK constraints (packages/db migrations 0106-0109). Kept as const-unions here so
 * the later feature waves (WS1/WS3/WS4/WS6) and apps/web validate against ONE source
 * of truth. No codegen yet — sync with the migration CHECKs by hand (repo convention).
 *
 * [Source: packages/db/migrations/0106_berecht_additive_columns.sql]
 * [Source: packages/db/migrations/0108_email_sync_blacklist.sql]
 * [Source: meetings/2026-08-24_ANALYSIS_berechtigungskonzept.md §2.8]
 */

/**
 * Email synchronisation depth per mail account (Bereich 1, decision 6).
 * 1 = Voll (full content) · 2 = Titel+Metadaten · 3 = nur Metadaten.
 * Higher number = less content synced. Capped per tenant by tenant.max_email_sync_level.
 * Sync with: user_email_sync_settings.sync_level / communication.sync_level_captured /
 * comm_release_request.sync_level_at_request / tenant.max_email_sync_level CHECKs.
 */
export type EmailSyncLevel = 1 | 2 | 3;

/** All valid email sync levels, in order. */
export const EMAIL_SYNC_LEVELS: readonly EmailSyncLevel[] = [1, 2, 3] as const;

/**
 * German labels for the 3 sync levels (Bereich 1, §2.2 table) — one source of truth
 * for the settings selector + any admin surface. Kept here (not in web) so api and web
 * agree on the wording.
 */
export const EMAIL_SYNC_LEVEL_LABELS: Record<EmailSyncLevel, string> = {
  1: 'Voller Zugriff',
  2: 'Metadaten mit Header',
  3: 'Nur Teilnehmer & Zeit',
} as const;

/** One-line description of what each sync level actually stores (for the UI). */
export const EMAIL_SYNC_LEVEL_DESCRIPTIONS: Record<EmailSyncLevel, string> = {
  1: 'Betreff, Teilnehmer, Inhalt und Anhänge werden vollständig synchronisiert.',
  2: 'Betreff und Teilnehmer werden synchronisiert, Inhalt und Anhänge nicht.',
  3: 'Nur Teilnehmer und Zeitpunkt: kein Betreff, kein Inhalt, keine Anhänge.',
} as const;

/** Narrow an arbitrary number to a valid EmailSyncLevel (1/2/3), else null. */
export function toEmailSyncLevel(value: number | null | undefined): EmailSyncLevel | null {
  return value === 1 || value === 2 || value === 3 ? value : null;
}

/**
 * The content projection a given sync level applies when a mail is ingested
 * (Bereich 1, §2.2). Higher level = less content persisted:
 *   - Stufe 1 Voller Zugriff        → subject + body + attachments (nothing removed).
 *   - Stufe 2 Metadaten mit Header  → subject kept; body + attachments NOT persisted.
 *   - Stufe 3 Nur Teilnehmer & Zeit → NO subject, NO body, NO attachments.
 * Participants (From/To/CC) + date/time are ALWAYS kept, at every level.
 *
 * Pure + shared so the api ingestion path and any test agree on ONE truth table.
 */
export interface EmailContentInput {
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
}
export interface EmailContentProjection {
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  /** false at Stufe 2/3 → the ingestion path skips persisting (and fetching) attachments. */
  includeAttachments: boolean;
}
export function projectEmailContentForSyncLevel(
  level: EmailSyncLevel,
  content: EmailContentInput,
): EmailContentProjection {
  // Stufe 1: everything. Stufe 2: metadata + subject, no body/attachments.
  // Stufe 3: only participants + time → subject/body/attachments all dropped.
  const keepSubject = level <= 2;
  const keepBody = level === 1;
  return {
    subject: keepSubject ? content.subject : null,
    bodyText: keepBody ? content.bodyText : null,
    bodyHtml: keepBody ? content.bodyHtml : null,
    includeAttachments: level === 1,
  };
}

/**
 * The anonymized list entry a Stufe-3 mail renders as (Bereich 1, §2.2):
 *   "E-Mail zwischen [Teilnehmer] am [Datum/Zeit]".
 * `participants` + `dateTime` are pre-formatted by the caller (the caller owns
 * locale/date formatting); this only guarantees the fixed wording is identical
 * everywhere it is shown.
 */
export function formatAnonymizedEmailEntry(participants: string, dateTime: string): string {
  const who = participants.trim() || 'unbekannten Teilnehmern';
  return `E-Mail zwischen ${who} am ${dateTime}`;
}

// ─── Wire shapes: user sync-level settings + tenant cap (Bereich 1) ────────────

/** One connected mailbox's per-account sync setting (GET /email-sync/settings). */
export interface EmailSyncMailboxSetting {
  mailbox_connection_id: string;
  provider: string;
  email: string;
  /** The level applied to THIS account (explicit per-account row, else the user default). */
  sync_level: EmailSyncLevel;
  /** true when this account has its own explicit row (vs inheriting the default). */
  is_explicit: boolean;
  /**
   * P3 (Berechtigungskonzept V3.0): true when this is a SHARED inbox. For a shared inbox
   * ANY member with access may set the sync level (the setting is one canonical row keyed
   * on the connection owner); false = a personal mailbox the caller owns.
   */
  is_shared: boolean;
  /**
   * P3: true when the caller is the OWNER (connecting broker) of this mailbox; false when
   * the caller is a non-owning MEMBER of a shared inbox. Owned + non-shared for personal
   * mailboxes (the pre-P3 case). Lets the UI label a shared inbox distinctly.
   */
  is_owner: boolean;
}

/**
 * GET /email-sync/settings — the caller's sync levels for their own mailboxes + the
 * tenant cap. `default_sync_level` is the fallback applied to any account without an
 * explicit per-account row.
 */
export interface EmailSyncSettingsResponse {
  max_email_sync_level: EmailSyncLevel;
  default_sync_level: EmailSyncLevel;
  mailboxes: EmailSyncMailboxSetting[];
}

/**
 * PUT /email-sync/settings body. `mailbox_connection_id: null` sets the caller's
 * DEFAULT level (applies to accounts without an explicit row).
 */
export interface SetEmailSyncLevelInput {
  mailbox_connection_id: string | null;
  sync_level: EmailSyncLevel;
}

/** GET/PUT /admin/email-sync-cap — the tenant-admin cap on user sync levels. */
export interface EmailSyncCapResponse {
  max_email_sync_level: EmailSyncLevel;
}
export interface SetEmailSyncCapInput {
  max_email_sync_level: EmailSyncLevel;
}

/**
 * Audit event severity (migration 0106, audit_log.severity).
 * 'high' tags elevated system-team + Kontrollfunktion views; everything else is 'normal'.
 */
export type AuditSeverity = 'normal' | 'high';

/** All valid audit severities. */
export const AUDIT_SEVERITIES: readonly AuditSeverity[] = ['normal', 'high'] as const;

/**
 * Blocklist rule tier (Bereich 1, 3.12, migration 0108, email_blacklist_rules.level).
 * Cumulative top-down: platform ⊇ tenant ⊇ user. Rules only ever TIGHTEN
 * ("verschärfen ja, lockern nein").
 */
export type EmailBlacklistLevel = 'platform' | 'tenant' | 'user';

/** All valid blocklist tiers, top-down. */
export const EMAIL_BLACKLIST_LEVELS: readonly EmailBlacklistLevel[] = [
  'platform',
  'tenant',
  'user',
] as const;

/**
 * Blocklist visibility scope (migration 0108, email_blacklist_rules.visibility_scope).
 * 'never_sync' = the mail never enters Brokly · 'metadata_only' = only metadata is
 * synced · 'owner_only' = visible to the account owner alone.
 */
export type EmailBlacklistVisibilityScope = 'never_sync' | 'metadata_only' | 'owner_only';

/** All valid blocklist visibility scopes. */
export const EMAIL_BLACKLIST_VISIBILITY_SCOPES: readonly EmailBlacklistVisibilityScope[] = [
  'never_sync',
  'metadata_only',
  'owner_only',
] as const;

/**
 * Freigabe grant duration preset (Bereich 1, 3.6, migration 0106, comm_access_grant.granted_duration).
 * The EFFECTIVE expiry stays comm_access_grant.expires_at (NULL = permanent); this
 * preset records WHICH choice the owner made so the UI can render "für 7 Tage".
 */
export type FreigabeGrantDuration = 'permanent' | '1_day' | '7_days' | '30_days' | 'until_date';

/** All valid Freigabe grant duration presets. */
export const FREIGABE_GRANT_DURATIONS: readonly FreigabeGrantDuration[] = [
  'permanent',
  '1_day',
  '7_days',
  '30_days',
  'until_date',
] as const;

/**
 * The internal party a Freigabe-Anfrage is addressed to (Bereich 1, 3.3):
 * the SENDER (internal from) or a RECIPIENT (internal to/cc) — the party who OWNS
 * the mail. When both exist the requester picks; stored on
 * comm_release_request.target_role for the audit trail.
 */
export type FreigabeTargetRole = 'sender' | 'recipient';

/** All valid Freigabe target roles. */
export const FREIGABE_TARGET_ROLES: readonly FreigabeTargetRole[] = ['sender', 'recipient'] as const;

/** Narrow an arbitrary string to a valid FreigabeTargetRole, else null. */
export function toFreigabeTargetRole(value: string | null | undefined): FreigabeTargetRole | null {
  return value === 'sender' || value === 'recipient' ? value : null;
}

/**
 * Compute the effective `expires_at` (ms epoch) for a granted Freigabe decision from
 * the chosen duration preset (Bereich 1, 3.3). `permanent` → null (no expiry). A timed
 * preset → now + N days. `until_date` → the caller-supplied ISO date (validated upstream).
 * Pure — the caller passes `now` so the value is deterministic + testable.
 */
export function freigabeExpiresAt(
  duration: FreigabeGrantDuration,
  now: Date,
  untilDate: string | null,
): Date | null {
  switch (duration) {
    case 'permanent':
      return null;
    case '1_day':
      return new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000);
    case '7_days':
      return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    case '30_days':
      return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    case 'until_date':
      return untilDate ? new Date(untilDate) : null;
  }
}

// ─── 3-tier Blockliste — cumulative cascade + matcher (Bereich 1, 3.5) ──────────

/**
 * Strictness rank of a blocklist visibility scope (Bereich 1, 3.5). Higher = more
 * restrictive. The cascade always applies the STRICTEST matching scope across all
 * levels (platform ⊇ tenant ⊇ user), and a lower-level rule may only ever TIGHTEN
 * ("verschärfen ja, lockern nie").
 *   never_sync (3)    — the mail never enters Brokly (strictest).
 *   metadata_only (2) — synced but content-stripped + owner-only visibility.
 *   owner_only (1)    — synced with content but owner-only visibility (least strict).
 */
export const EMAIL_BLACKLIST_SCOPE_STRICTNESS: Record<EmailBlacklistVisibilityScope, number> = {
  never_sync: 3,
  metadata_only: 2,
  owner_only: 1,
} as const;

/** true iff `a` is at least as strict as `b` (a can validly replace/tighten b). */
export function blacklistScopeAtLeastAsStrict(
  a: EmailBlacklistVisibilityScope,
  b: EmailBlacklistVisibilityScope,
): boolean {
  return EMAIL_BLACKLIST_SCOPE_STRICTNESS[a] >= EMAIL_BLACKLIST_SCOPE_STRICTNESS[b];
}

/** The stricter of two scopes (used to fold a cascade of matching rules). */
export function stricterBlacklistScope(
  a: EmailBlacklistVisibilityScope,
  b: EmailBlacklistVisibilityScope,
): EmailBlacklistVisibilityScope {
  return blacklistScopeAtLeastAsStrict(a, b) ? a : b;
}

/** One blocklist rule as needed for matching (a subset of email_blacklist_rules). */
export interface BlacklistRuleMatchInput {
  level: EmailBlacklistLevel;
  pattern: string;
  visibility_scope: EmailBlacklistVisibilityScope;
}

/** "Lara Meier <lara@kunde.ch>" → "lara@kunde.ch" (lowercased); null if it holds no address. */
export function extractEmailAddress(raw: string | null | undefined): string | null {
  const addr = bareAddress(raw);
  return addr !== '' && isEmailAddress(addr) ? addr : null;
}

/** "Name <a@b.ch>" -> "a@b.ch", lowercased and trimmed; '' for a blank value. */
function bareAddress(raw: string | null | undefined): string {
  const value = (raw ?? '').trim().toLowerCase();
  const angle = value.match(/<([^>]+)>/);
  return (angle ? angle[1]! : value).trim();
}

/** A single e-mail address (local@domain.tld), not a bare domain. */
export function isEmailAddress(value: string): boolean {
  return /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(value.trim());
}

/**
 * Does a blocklist `pattern` match a sender address? (Bereich 1, 3.5 — matcher lives
 * in the app, not the DB.) Case-insensitive. Two shapes:
 *   - pattern contains '@' → exact ADDRESS match ("kanzlei@hausarzt.ch").
 *   - pattern is a DOMAIN  → the sender's domain equals it OR is a sub-domain of it
 *                            ("hausarzt.ch" matches a@hausarzt.ch and a@x.hausarzt.ch).
 * A null/blank sender never matches (conservative — an unparseable sender is not blocked
 * by a domain rule; never_sync of unparseable senders is out of scope here).
 */
export function matchesBlacklistPattern(pattern: string, senderAddress: string | null): boolean {
  // The addr-spec of a display-name wrapper "Name <a@b.ch>" → "a@b.ch" (as extractEmailAddress).
  const addr = bareAddress(senderAddress);
  const pat = pattern.trim().toLowerCase();
  if (addr === '' || pat === '') return false;

  if (pat.includes('@')) {
    return addr === pat;
  }
  // Domain rule: compare the sender's domain (after the last '@').
  const at = addr.lastIndexOf('@');
  const domain = at >= 0 ? addr.slice(at + 1) : addr;
  return domain === pat || domain.endsWith('.' + pat);
}

/**
 * Fold the applicable blocklist rules for a sender into the single EFFECTIVE scope
 * (Bereich 1, 3.5 cumulative cascade). Returns the STRICTEST matching scope across all
 * levels, or null when nothing matches. The caller supplies the already-applicable rule
 * set (platform + tenant + the mailbox-owner's own user rules — RLS/ownership resolved
 * server-side); this fn is pure so the sync path AND the read path share ONE truth.
 */
export function resolveEffectiveBlacklistScope(
  rules: readonly BlacklistRuleMatchInput[],
  senderAddress: string | null,
): EmailBlacklistVisibilityScope | null {
  let effective: EmailBlacklistVisibilityScope | null = null;
  for (const rule of rules) {
    if (!matchesBlacklistPattern(rule.pattern, senderAddress)) continue;
    effective = effective === null ? rule.visibility_scope : stricterBlacklistScope(effective, rule.visibility_scope);
  }
  return effective;
}

// ─── Wire shapes: 3-tier blocklist admin surface (Bereich 1, 3.5) ───────────────

/** One blocklist rule as returned to the admin/user surface (no tenant_id on the wire). */
export interface EmailBlacklistRuleDto {
  id: string;
  level: EmailBlacklistLevel;
  /** Set only for a user-level rule (the owning broker's app_user id). */
  owner_user_id: string | null;
  pattern: string;
  visibility_scope: EmailBlacklistVisibilityScope;
  created_at: string;
  /** true when the caller may NOT edit/delete this rule (platform rules, or a
   *  tenant rule seen by a non-admin, or another user's rule) — display read-only. */
  read_only: boolean;
  /** POST /email-blocklist only: true when a firm-wide admin rule already (fully) covers the address,
   *  so NO personal rule was created and the returned rule is that covering admin rule (read-only).
   *  The UI shows "already blocked firm-wide" instead of a block confirmation. */
  firm_wide_blocked?: boolean;
  /** P2 hardening 04: true when a tenant rule additionally hides the sender's PAST mail firm-wide
   *  via an active posteingang_sender_hide (scope tenant) row ("auch bisherige"). Tenant rules only. */
  hidden_past?: boolean;
}

/** GET /email-blocklist — platform (read-only) + tenant + the caller's own user rules. */
export interface EmailBlacklistListResponse {
  rules: EmailBlacklistRuleDto[];
  /** true when the caller may manage TENANT-level rules (admin). */
  can_manage_tenant: boolean;
}

/** POST /email-blocklist body. `level` decides who may create it (tenant=admin, user=self). */
export interface CreateEmailBlacklistRuleInput {
  level: Exclude<EmailBlacklistLevel, 'platform'>;
  pattern: string;
  visibility_scope: EmailBlacklistVisibilityScope;
  /** P2 hardening 04: tenant-level only — also hide the sender's PAST mail firm-wide ("auch
   *  bisherige"). Ignored on the idempotent/covering branches; a user-level request with
   *  hide_past=true is a 400 (hide_past_tenant_only). */
  hide_past?: boolean;
}
